import {
  addOrReplaceAndRunAnalysisStep,
  deleteAnalysisSteps,
  updateAndRunAnalysisStep,
} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {DataFrameRenameColumnOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {withLoadingScreen} from 'util/Promises';

import {
  RightSidebarItemState,
  setRightSidebarItemIsUpdating,
  updateRightSidebarItemOnAddingStep,
} from './DataFrameHeaderMenuUtil';

const LOADING_MESSAGE = 'Renaming column';

async function updateRenameColumnStep(
  analysis: AnalysisGraphQL,
  analysisClientStateId: string,
  renameColumnStep: GetAnalysisPipelineStep,
  systemName: string,
  newColumnName?: string
): Promise<void> {
  const previousRenameColumns = renameColumnStep.config?.variables.find(
    (variable) => variable.name === 'renameColumns'
  )?.value as Array<{column: string; name: string}> | undefined;
  const columnsConfigToKeep = previousRenameColumns
    ? previousRenameColumns.flat().filter(({column}) => column !== systemName)
    : [];
  const renameColumns = newColumnName
    ? [...columnsConfigToKeep, {column: systemName, name: newColumnName}]
    : columnsConfigToKeep;
  if (renameColumns.length === 0) {
    await deleteAnalysisSteps(
      analysis,
      {
        stepIds: [renameColumnStep.id],
        behavior: DeletePipelineStepsBehavior.NO_INFERENCE,
      },
      analysisClientStateId
    );
  } else {
    await updateAndRunAnalysisStep(analysis.id, {
      stepId: renameColumnStep.id,
      stepConfig: {renameColumns},
    });
  }
}

async function addRenameColumnStep(
  analysis: AnalysisGraphQL,
  columnSystemName: string,
  newColumnName: string,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>,
  rightSidebarItemState?: RightSidebarItemState
): Promise<void> {
  if (rightSidebarItemState) {
    setRightSidebarItemIsUpdating(rightSidebarItemState);
  }
  const newStep = await addOrReplaceAndRunAnalysisStep(analysis.id, {
    stepType: PipelineStepType.RENAME_COLUMNS,
    stepVersion: 0,
    stepConfig: {renameColumns: [{column: columnSystemName, name: newColumnName}]},
    stepInputParents: {[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference]},
    ...(inputDataFrameId == null ? {} : {stepInput: {inputDataFrameId}}),
    ...(outputDataReferences == null
      ? {}
      : {stepOutputChildren: {[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: outputDataReferences}}),
  });
  if (rightSidebarItemState) {
    updateRightSidebarItemOnAddingStep(newStep, rightSidebarItemState);
  }
}

export function getRenameColumnOptions(
  analysis: AnalysisGraphQL,
  analysisClientStateId: string,
  step: GetAnalysisPipelineStep,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>,
  rightSidebarItemState?: RightSidebarItemState
): DataFrameRenameColumnOptions {
  const inputStep = getLegacyStepFromAnalysis(analysis, inputDataReference.stepId);
  inputDataFrameId =
    inputDataFrameId ?? findAndAssertSingleStepDataFrameId(inputStep, inputDataReference.stepDataName);

  if (step.definition.stepType === PipelineStepType.RENAME_COLUMNS) {
    const renameColumn = async (columnSystemName: string, newColumnName?: string): Promise<void> => {
      await withLoadingScreen(
        updateRenameColumnStep(analysis, analysisClientStateId, step, columnSystemName, newColumnName),
        LOADING_MESSAGE
      );
    };
    return {renameColumn};
  }
  // Previous step in chain is not a rename column step, so we make an new transform
  const renameColumn = async (columnSystemName: string, newColumnName?: string): Promise<void> => {
    await withLoadingScreen(
      addRenameColumnStep(
        analysis,
        columnSystemName,
        newColumnName!,
        inputDataReference,
        inputDataFrameId,
        outputDataReferences,
        rightSidebarItemState
      ),
      LOADING_MESSAGE
    );
  };
  return {renameColumn};
}
